import React, { useContext, useMemo } from 'react';
import type { AlohaWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import { AuxFrame, AuxFrameContext } from '../AuxFrame';
import { HeaderPhoto } from './components/HeaderPhoto/HeaderPhoto';
import AuxFrameTitle from '../AuxFrame/components/AuxFrameTitle';
import { Box } from '@withjoy/joykit';
import { InlineEditor } from '@apps/guest/shared/components/InlineEditor';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { AnimatedContainer } from './components/AnimatedContainer/AnimatedContainer';
import { CoverPhoto } from './components/CoverPhoto/CoverPhoto';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useSkipInitialAnimation } from '@apps/guest/packages/layout-engine/hooks/useSkipInitialAnimation';
import { useResponsive } from '@shared/utils/hooks/useResponsive';

export const CustomizableHeader: React.FC<{
  eventHandle: string;
  websiteHeader: NonNullable<AlohaWebsiteHeaderSettings['websiteHeader']>;
  telemetryPhotoEditor: {
    onEditPhotoOpenedChange: (opened: boolean) => void;
  };
  fontColor?: string;
}> = ({ children, websiteHeader }) => {
  const { page, pages, pageSlug, photo, pageId } = useContext(AuxFrameContext);
  const { checkIsWelcomePage } = useWelcomePage();
  const { isWelcomePage, welcomePageId } = useMemo(() => {
    const welcomePage = pages.find(page => checkIsWelcomePage(page.pageSlug, page.type).isWelcomePage);
    const welcomePageId = welcomePage?.id || '';
    return {
      isWelcomePage: pageId === welcomePageId,
      welcomePageId,
    };
  }, [pages, pageId, checkIsWelcomePage]);

  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });

  const handleEditPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  };
  const handleEditCoverPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editPhoto', inlineEditData: { photoPresentation: null, enablePhotoLayout: false, photo, pageId, page, pageSlug } },
    });
  };

  // Track whether the initial mount cycle has completed. When the feature is
  // enabled, skip the initial animation to avoid react-spring triggering a fade
  // when isWelcomePage flips from false → true as AuxFrame resolves pageId.
  // After mount, AnimatedContainer handles page transition animations normally.
  const skipAnimation = useSkipInitialAnimation();
  const hasCompletedFirstMount = useIsMounted();

  const isRenderingWebsiteHeaderLayout = isWelcomePage || isMobile;

  return (
    <AuxFrame>
      {websiteHeader && (
        <>
          <Box zIndex={100}>
            <AuxFrameTitle
              x={'left'}
              y={'bottom'}
              textLight={true}
              title={websiteHeader?.eventDisplayName}
              subtitle={websiteHeader?.greetingMessage}
              welcomePageId={welcomePageId}
              page={page}
              pageSlug={pageSlug || ''}
              zIndex={1}
            />
          </Box>
          <InlineEditor
            elementLabel="Photo"
            wrapperType="actionInside"
            actionData={{
              editPhoto: isRenderingWebsiteHeaderLayout ? handleEditPhotoClick : handleEditCoverPhotoClick,
            }}
            minHeight="100vh"
            componentName="headerPhoto"
            pageName={page}
            pageSlug={pageSlug || ''}
          >
            <AnimatedContainer
              id={isRenderingWebsiteHeaderLayout ? 'header-photo' : photo?.id || ''}
              skipAnimation={skipAnimation && !hasCompletedFirstMount}
            >
              {isRenderingWebsiteHeaderLayout ? (
                <HeaderPhoto headerPhoto={websiteHeader?.headerPhoto} />
              ) : photo ? (
                <CoverPhoto photo={photo} />
              ) : (
                <Box
                  width="100%"
                  height="100vh"
                />
              )}
            </AnimatedContainer>
          </InlineEditor>
        </>
      )}
      {children}
    </AuxFrame>
  );
};
